import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/services", label: t("nav.services") },
    { to: "/portfolio", label: t("nav.portfolio") },
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/about", label: t("nav.about") },
    { to: "/consultations", label: t("nav.consultations") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-[#131211]/85 backdrop-blur-xl border-b border-edge"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20">
          <Link
            data-testid="navbar-logo"
            to="/"
            className="text-ink hover:text-brand transition-colors"
          >
            <Logo size={28} />
          </Link>

          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {links.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 + i * 0.05, duration: 0.5 }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `text-sm font-light tracking-wide transition-colors underline-link ${
                      isActive ? "text-brand" : "text-ink hover:text-brand"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
          </nav>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1 text-eyebrow">
              <button
                data-testid="lang-switch-en"
                onClick={() => setLang("en")}
                className={`px-1 transition-colors ${
                  lang === "en" ? "text-brand" : "text-ink-muted hover:text-ink"
                }`}
                aria-label="Switch to English"
              >
                EN
              </button>
              <span className="text-ink-dim">/</span>
              <button
                data-testid="lang-switch-es"
                onClick={() => setLang("es")}
                className={`px-1 transition-colors ${
                  lang === "es" ? "text-brand" : "text-ink-muted hover:text-ink"
                }`}
                aria-label="Switch to Spanish"
              >
                ES
              </button>
            </div>

            <Link
              to="/consultations"
              data-testid="navbar-book-cta"
              className="hidden md:inline-flex items-center px-6 py-3 bg-brand text-ink-inverse text-sm font-medium tracking-wide hover:bg-brand-hover transition-colors"
            >
              {t("nav.bookNow")}
            </Link>

            <button
              data-testid="navbar-mobile-toggle"
              className="lg:hidden text-ink"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              className="lg:hidden pb-8 overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="flex flex-col gap-5">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mnav-${l.to.replace("/", "") || "home"}`}
                    className={({ isActive }) =>
                      `text-xl font-display ${
                        isActive ? "text-brand" : "text-ink"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/consultations"
                  data-testid="mobile-book-cta"
                  className="mt-4 inline-flex items-center justify-center px-6 py-4 bg-brand text-ink-inverse text-sm font-medium"
                >
                  {t("nav.bookNow")}
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
